import { I18NextPipe } from 'angular-i18next';
import { orderByParam, orderDirectionDelimiter, SortDirection, SortParams } from 'mbs-ui-kit/models/sort';

export interface QuerySortParams {
  [orderByParam]: string;
}

export function getSortParams(sorting: SortParams, defaultSorting?: SortParams, delimiter = orderDirectionDelimiter): QuerySortParams {
  if (defaultSorting && sorting?.column === defaultSorting.column && sorting?.direction === defaultSorting.direction)
    return { [orderByParam]: null };

  return {
    [orderByParam]: sorting && sorting.column && sorting?.direction ? `${sorting.column}${delimiter}${sorting?.direction}` : null
  };
}

export function extractSortParams(sortParam: string): SortParams {
  if (!sortParam) return null;

  const [column, direction] = sortParam.split(':');
  return { column, direction: direction as SortDirection };
}

export interface Page {
  page: string[];
  id: number;
}

export type PagingParams = {
  currentPageNumber: number;
  perPage: number;
};

export function getPagingParams(pagination: PagingParams): QueryPagingParams {
  return {
    offset: (pagination.currentPageNumber - 1) * pagination.perPage,
    limit: pagination.perPage
  };
}

export type QueryPagingParams = {
  offset: number;
  limit?: number;
};

export type QueryFiltersParams = {
  [param: string]: string | number | boolean | string[] | number[];
};

export type QueryParams = Partial<QuerySortParams> & Partial<QueryPagingParams> & Partial<QueryFiltersParams>;

export class PagedResponse<T> {
  data: T[];
  total: number;
}

export type ClearPagingState = {
  loaded: boolean;
  loading: boolean;
  total: number;
};

export type PagingState = ClearPagingState &
  PagingParams & {
    sort: SortParams;
  };

export type AllPagesState = {
  [page: number]: string[];
};

export type EntitiesLoadingState = {
  [id: string]: boolean;
};

export type ExtendedPagingState<T, F> = {
  paging: ClearPagingState &
    PagingParams & {
      pages: AllPagesState;
      filters: T;
      isErrorState: boolean;
      obsolescenceMark: number;
    };
  data: ClearPagingState & {
    // no filters, no sorting
    entities: string[];
    isErrorState: boolean;
    obsolescenceMark: number;
  };
  loading: EntitiesLoadingState;
  custom: F;
};

export enum PerPageSize {
  Small = 10,
  Medium = 25,
  Large = 50,
  ExtraLarge = 75,
  Huge = 100
}

export function tableSelectedEntitiesText(params: {
  i18nPipe: I18NextPipe;
  entityPluralKey: string;
  count: number;
  isSelectAllOnAllPages: boolean;
}): string {
  const selected = params.i18nPipe.transform('shared:common.selected', { format: 'title' });
  const entities = params.i18nPipe.transform(params.entityPluralKey, { count: params.count });
  const page = params.i18nPipe.transform('shared:paging.' + (params.isSelectAllOnAllPages ? 'onAllPages' : 'onThisPage'));

  return `${selected} ${params.count} ${entities} ${page}`;
}

export function tableSelectAllEntitiesText(params: { i18nPipe: I18NextPipe; entityPluralKey: string; total: number }): string {
  const selectAll = params.i18nPipe.transform('shared:paging.selectAll');
  const entities = params.i18nPipe.transform(params.entityPluralKey, { count: params.total });

  return `${selectAll} ${entities}: ${params.total}`;
}
